import { Check, X } from "lucide-react";
import { Reveal } from "./Reveal";
import { scrollToEl } from "@/lib/scroll";

// Side-by-side comparison of the four coaching programs.
// Helps prospects self-select without decision fatigue.
const PROGRAMS = [
  {
    key: "in-person",
    name: "In-Person",
    tagline: "Max accountability. Every rep coached.",
    price: "From $299/mo",
    accent: false,
    features: {
      "1-on-1 coach in room": true,
      "Custom training program": true,
      "Nutrition guidance": true,
      "Weekly check-ins": true,
      "Form correction on the spot": true,
      "Access to bootcamps": "Add-on",
      "Best for": "Fastest results, hands-on coaching",
    },
  },
  {
    key: "hybrid",
    name: "Hybrid",
    tagline: "Best of both worlds.",
    price: "From $399/mo",
    accent: true,
    features: {
      "1-on-1 coach in room": "2x/month",
      "Custom training program": true,
      "Nutrition guidance": true,
      "Weekly check-ins": true,
      "Form correction on the spot": "Partial",
      "Access to bootcamps": true,
      "Best for": "Busy pros who want structure + flexibility",
    },
  },
  {
    key: "online",
    name: "Online",
    tagline: "Coach in your pocket.",
    price: "From $199/mo",
    accent: false,
    features: {
      "1-on-1 coach in room": false,
      "Custom training program": true,
      "Nutrition guidance": true,
      "Weekly check-ins": true,
      "Form correction on the spot": "Via video",
      "Access to bootcamps": false,
      "Best for": "Self-motivated, remote, or travelling clients",
    },
  },
  {
    key: "bootcamp",
    name: "Bootcamp",
    tagline: "Group energy. Serious sweat.",
    price: "From $99/mo",
    accent: false,
    features: {
      "1-on-1 coach in room": false,
      "Custom training program": false,
      "Nutrition guidance": false,
      "Weekly check-ins": false,
      "Form correction on the spot": "Group setting",
      "Access to bootcamps": "Unlimited",
      "Best for": "High-energy group workouts on a budget",
    },
  },
];

const ROWS = [
  "1-on-1 coach in room",
  "Custom training program",
  "Nutrition guidance",
  "Weekly check-ins",
  "Form correction on the spot",
  "Access to bootcamps",
  "Best for",
];

function Cell({ value }) {
  if (value === true) {
    return (
      <span className="inline-flex items-center gap-1 text-[#005EEA] font-mono-accent text-xs">
        <Check size={16} strokeWidth={2.5} /> YES
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex items-center gap-1 text-white/25 font-mono-accent text-xs">
        <X size={16} strokeWidth={2.5} /> —
      </span>
    );
  }
  return <span className="text-white/85 text-xs sm:text-sm">{value}</span>;
}

export default function ProgramCompare() {
  return (
    <section
      id="compare"
      className="relative py-24 sm:py-32 bg-[#050505] border-t border-white/5"
      data-testid="program-compare"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <Reveal>
          <div className="mb-12 flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="font-mono-accent text-xs text-[#005EEA] tracking-[0.3em] mb-3">
                — 04 · COMPARE
              </div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.9] uppercase">
                Pick your <span className="text-[#005EEA]">path.</span>
              </h2>
            </div>
            <p className="text-white/60 text-sm sm:text-base max-w-md">
              Not sure which program fits? Here&apos;s every option side-by-side. Still unsure —
              book a free consult and we&apos;ll pick together.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="overflow-x-auto -mx-4 sm:mx-0" data-testid="compare-scroll">
            <div className="min-w-[720px] px-4 sm:px-0">
              {/* Header row */}
              <div className="grid grid-cols-[160px_repeat(4,minmax(0,1fr))] border border-white/10 bg-black/40">
                <div className="p-4 border-r border-white/10" />
                {PROGRAMS.map((p) => (
                  <div
                    key={p.key}
                    className={`p-4 border-r border-white/10 last:border-r-0 ${
                      p.accent ? "bg-[#005EEA]/10" : ""
                    }`}
                    data-testid={`compare-header-${p.key}`}
                  >
                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                      <h3 className="font-display text-xl sm:text-2xl uppercase">{p.name}</h3>
                      {p.accent && (
                        <span className="font-mono-accent text-[10px] tracking-[0.2em] text-[#005EEA]">
                          POPULAR
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-white/50 mt-1">{p.tagline}</div>
                    <div className="font-mono-accent text-xs text-white/80 mt-2">{p.price}</div>
                  </div>
                ))}
              </div>

              {/* Feature rows */}
              {ROWS.map((row, idx) => (
                <div
                  key={row}
                  className={`grid grid-cols-[160px_repeat(4,minmax(0,1fr))] border-x border-b border-white/10 ${
                    idx % 2 === 0 ? "bg-white/[0.02]" : "bg-transparent"
                  }`}
                >
                  <div className="p-4 border-r border-white/10 text-xs sm:text-sm text-white/60 font-mono-accent uppercase tracking-wide">
                    {row}
                  </div>
                  {PROGRAMS.map((p) => (
                    <div
                      key={p.key + row}
                      className={`p-4 border-r border-white/10 last:border-r-0 ${
                        p.accent ? "bg-[#005EEA]/[0.06]" : ""
                      }`}
                    >
                      <Cell value={p.features[row]} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-4 items-center justify-between">
            <p className="text-white/50 text-sm">
              Swipe / scroll horizontally on mobile to compare all four →
            </p>
            <button
              onClick={() => scrollToEl("#consult")}
              className="font-mono-accent text-xs uppercase tracking-[0.2em] bg-[#005EEA] text-white px-8 py-4 hover:bg-[#0047b3] transition-colors shadow-[0_0_30px_rgba(0,94,234,0.4)]"
              data-testid="compare-consult-btn"
            >
              Still Unsure? Book Free Consult →
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
